import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PageHeader, KindChip, Avatar, ReviewPill, Icon,
  primaryBtn, hdrCell,
} from "@/components/admin-ui";
import { PopoverMenu, useToast } from "@/components/ui-prims";
import { useAdminArticles } from "@/lib/articles-hooks";
import { useAuthorMap, useAuthors } from "@/lib/authors-hooks";
import { HOUSE_AUTHOR_ID } from "@/constants/articles";

type TabKey = "all" | "queue" | "approved" | "published" | "archived";

const PAGE_SIZE = 25;

export function ArticlesListPage() {
  // URL-driven default tab so deep-links from the Review queue header
  // (e.g. "Cleared (last 30d)" → /editorial/articles?tab=published)
  // land on the right view. Falls back to "all" for the bare URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return (t === "queue" || t === "approved" || t === "published" || t === "archived") ? (t as TabKey) : "all";
  })();
  const [tab, setTab] = useState<TabKey>(initialTab);
  // Keep the URL in sync so a refresh preserves the tab and the
  // back/forward buttons work as expected.
  useEffect(() => {
    if (tab === "all") {
      if (searchParams.has("tab")) {
        const next = new URLSearchParams(searchParams);
        next.delete("tab");
        setSearchParams(next, { replace: true });
      }
    } else if (searchParams.get("tab") !== tab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", tab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);
  // Author filter is keyed by real auth_users id (UUID) now, not the
  // hardcoded mock AuthorKey enum. "All" is the wildcard.
  const [author, setAuthor] = useState<string>("All");
  // Search is debounced — `searchInput` is the raw onChange value,
  // `searchQuery` is what we actually filter against.
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 250);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const [page, setPage] = useState(1);
  // Reset to page 1 whenever any filter changes.
  useEffect(() => { setPage(1); }, [tab, author, searchQuery]);

  const navigate = useNavigate();
  const toast = useToast();

  const { rows: all } = useAdminArticles({});
  // Author lookup map. Falls back to a synthetic "Unknown author"
  // entry so a row whose author_id isn't in the /admin/authors
  // response (deleted user, race) still renders gracefully.
  const { resolve: resolveAuthor } = useAuthorMap();
  // Live author roster for the filter dropdown.
  const { authors: authorList } = useAuthors();
  const counts = {
    all: all.length,
    queueLike: all.filter(a => ["awaiting_review", "in_review", "changes_requested"].includes(a.state)).length,
    approved:  all.filter(a => ["approved", "scheduled"].includes(a.state)).length,
    published: all.filter(a => a.state === "published").length,
    archived:  all.filter(a => a.state === "archived" || a.state === "rejected").length,
  };

  const filtered = all
    .filter(a => {
      if (tab === "all") return true;
      if (tab === "queue") return ["awaiting_review", "in_review", "changes_requested"].includes(a.state);
      if (tab === "approved") return ["approved", "scheduled"].includes(a.state);
      if (tab === "published") return a.state === "published";
      if (tab === "archived") return a.state === "archived" || a.state === "rejected";
      return true;
    })
    .filter(a => (author === "All" ? true : a.author === author))
    .filter(a => {
      if (!searchQuery) return true;
      const hay = `${a.title} ${a.slug} ${a.tags.join(" ")}`.toLowerCase();
      return hay.includes(searchQuery);
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <>
      <PageHeader
        kicker="Editorial"
        title="All articles"
        sub="Every piece in flight — from desk drafts to the live Newsroom feed."
        actions={
          <button
            style={primaryBtn}
            type="button"
            onClick={() => toast("Article editor coming soon", { tone: "info" })}
          >
            {Icon.plus} New article
          </button>
        }
      />

      <div style={toolbar}>
        <div style={{ display: "flex", gap: 2 }}>
          {([
            ["all", "All", counts.all],
            ["queue", "In review", counts.queueLike],
            ["approved", "Approved", counts.approved],
            ["published", "Published", counts.published],
            ["archived", "Archived & rejected", counts.archived],
          ] as Array<[TabKey, string, number]>).map(([k, label, n]) => {
            const on = tab === k;
            return (
              <button
                key={k}
                onClick={() => setTab(k)}
                type="button"
                style={{
                  background: "transparent", border: "none", cursor: "pointer",
                  padding: "10px 12px 12px",
                  display: "inline-flex", alignItems: "center", gap: 7,
                  fontFamily: "var(--font-ui)", fontSize: 12.5,
                  fontWeight: on ? 500 : 400,
                  color: on ? "var(--accent)" : "var(--ink-3)",
                  borderBottom: "2px solid",
                  borderBottomColor: on ? "var(--accent)" : "transparent",
                  marginBottom: -1, letterSpacing: "-0.005em",
                }}
              >
                {label}
                <span
                  style={{
                    fontFamily: "var(--font-mono)", fontSize: 10, fontWeight: 600,
                    color: on ? "var(--accent)" : "var(--ink-4)",
                    background: on ? "var(--accent-soft)" : "var(--surface-2)",
                    padding: "1px 6px", borderRadius: 4,
                  }}
                >
                  {n}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1 }} />

        <div style={searchWrap}>
          {Icon.search}
          <input
            placeholder="Search title, tag, slug…"
            style={searchInputStyle}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <select
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          style={selectBox}
        >
          <option value="All">All authors</option>
          {/* The house identity is filtered out of /admin/authors (it
              is not an agent), so it is added as an explicit facet. */}
          <option value={HOUSE_AUTHOR_ID}>ProProp Data Lab</option>
          {authorList.map((entry) => (
            <option key={entry.id} value={entry.id}>{entry.display.name}</option>
          ))}
        </select>
      </div>

      <div style={tableWrap}>
        <div style={hdrRow}>
          <div><input type="checkbox" style={{ accentColor: "var(--accent)" }} /></div>
          <div>Article</div>
          <div>Status</div>
          <div>Author</div>
          <div style={{ textAlign: "right" }}>Submitted</div>
          <div style={{ textAlign: "center" }}>v</div>
          <div style={{ textAlign: "right" }}>Flags</div>
          <div style={{ textAlign: "right" }}>Comments</div>
          <div style={{ textAlign: "right" }}>Updated</div>
          <div></div>
        </div>
        {pageRows.map((a) => {
          const au = a.authorDisplay ?? resolveAuthor(a.author).display;
          return (
            <div
              key={a.id}
              style={dataRow}
              className="ops-row"
              onClick={() => navigate(`/editorial/articles/${a.id}`)}
            >
              <div onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" style={{ accentColor: "var(--accent)" }} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <KindChip kind={a.kind} />
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--ink-4)" }}>{a.slug}</span>
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-serif)", fontSize: 13.5, fontWeight: 500,
                    color: "var(--ink)", letterSpacing: "-0.01em",
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}
                >
                  {a.title}
                </div>
              </div>
              <div><ReviewPill state={a.state} /></div>
              <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                <Avatar initials={au.initials} tint={au.tint} size={22} title={au.name} />
                <span style={{ fontSize: 12, color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {au.name.split(" ")[0]}
                </span>
              </div>
              <div style={{ textAlign: "right", fontSize: 11.5, color: "var(--ink-3)" }}>{a.submitted}</div>
              <div style={{ textAlign: "center", fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-3)" }}>v{a.version}</div>
              <div style={{ textAlign: "right" }}>
                {a.flags > 0
                  ? <span style={flagChip(a.flags)}>{a.flags}</span>
                  : <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-4)" }}>—</span>}
              </div>
              <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 11.5, color: a.comments > 0 ? "var(--ink-2)" : "var(--ink-4)" }}>
                {a.comments > 0 ? a.comments : "—"}
              </div>
              <div style={{ textAlign: "right", fontSize: 11, color: "var(--ink-3)" }}>{a.submitted}</div>
              <div style={{ display: "flex", justifyContent: "flex-end" }} onClick={(e) => e.stopPropagation()}>
                <RowMenu articleId={a.id} slug={a.slug} />
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div
            style={{
              padding: "48px 0", textAlign: "center",
              fontFamily: "var(--font-serif)", fontStyle: "italic",
              color: "var(--ink-3)", fontSize: 14,
            }}
          >
            Nothing matches the current filter.
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "12px 26px 26px", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)",
        }}
      >
        <div>
          {filtered.length} of {all.length} articles · sorted by submitted (newest)
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            style={pagerBtn}
            type="button"
            disabled={safePage <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            ‹ Prev
          </button>
          <span style={{ alignSelf: "center" }}>Page {safePage} / {totalPages}</span>
          <button
            style={pagerBtn}
            type="button"
            disabled={safePage >= totalPages}
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          >
            Next ›
          </button>
        </div>
      </div>
    </>
  );
}

function RowMenu({ articleId, slug }: { articleId: string; slug: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  return (
    <>
      <button
        ref={btnRef}
        style={iconBtn}
        type="button"
        title="More"
        onClick={() => {
          const rect = btnRef.current?.getBoundingClientRect() ?? null;
          setAnchor(rect);
          setOpen(o => !o);
        }}
      >
        {Icon.more}
      </button>
      <PopoverMenu
        open={open}
        anchorRect={anchor}
        onClose={() => setOpen(false)}
        items={[
          {
            label: "Open",
            onClick: () => navigate(`/editorial/articles/${articleId}`),
          },
          {
            label: "Copy slug",
            onClick: async () => {
              try {
                await navigator.clipboard.writeText(slug);
                toast("Slug copied", { tone: "success" });
              } catch {
                toast("Copy failed — clipboard blocked", { tone: "error" });
              }
            },
          },
        ]}
      />
    </>
  );
}

const toolbar: CSSProperties = {
  display: "flex", alignItems: "center", gap: 9,
  padding: "0 26px", borderBottom: "1px solid var(--rule)",
  background: "var(--surface)",
};
const searchWrap: CSSProperties = {
  display: "flex", alignItems: "center", gap: 7,
  padding: "6px 11px", borderRadius: 7,
  background: "var(--bg)", border: "1px solid var(--rule)",
  color: "var(--ink-3)", width: 260, marginRight: 4,
};
const searchInputStyle: CSSProperties = {
  flex: 1, background: "transparent", border: "none", outline: "none",
  fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink)",
};
const selectBox: CSSProperties = {
  padding: "6px 9px", borderRadius: 6,
  background: "var(--bg)", border: "1px solid var(--rule)",
  color: "var(--ink-2)", fontFamily: "var(--font-ui)", fontSize: 12,
};
const tableWrap: CSSProperties = {
  margin: "14px 26px 0", borderRadius: 10,
  border: "1px solid var(--rule)", background: "var(--surface)", overflow: "hidden",
};
const rowGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "28px 1fr 150px 130px 130px 40px 70px 90px 110px 30px",
  gap: 12, alignItems: "center",
};
const hdrRow: CSSProperties = {
  ...rowGrid, padding: "10px 14px", ...hdrCell,
  background: "var(--surface-2)", borderBottom: "1px solid var(--rule)",
};
const dataRow: CSSProperties = {
  ...rowGrid, padding: "12px 14px", borderBottom: "1px solid var(--rule)",
};
const iconBtn: CSSProperties = {
  width: 26, height: 26, borderRadius: 5,
  border: "1px solid var(--rule)", background: "transparent",
  color: "var(--ink-3)", cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
};
const pagerBtn: CSSProperties = {
  padding: "5px 10px", borderRadius: 5,
  border: "1px solid var(--rule)", background: "var(--surface)",
  color: "var(--ink-2)", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 11.5,
};
const flagChip = (n: number): CSSProperties => ({
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  width: 22, height: 18, borderRadius: 4,
  background: n >= 3 ? "rgba(122,45,31,0.10)" : "rgba(138,90,0,0.12)",
  color: n >= 3 ? "#7a2d1f" : "#8a5a00",
  fontFamily: "var(--font-mono)", fontSize: 10.5, fontWeight: 700,
});
